import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api';
import type { CreateUserData, UpdateUserData, UpdateProfileData } from '../types';

const KEYS = {
  all: ['users'] as const,
  list: (params?: Record<string, unknown>) => [...KEYS.all, 'list', params] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  relatedRecords: (id: string) => [...KEYS.all, 'related-records', id] as const,
  therapists: () => [...KEYS.all, 'therapists'] as const,
  pending: () => [...KEYS.all, 'pending'] as const,
  profile: () => [...KEYS.all, 'profile'] as const,
  myHospitals: () => [...KEYS.all, 'my-hospitals'] as const,
};

export function useUsers(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: async () => {
      const { data } = await usersApi.list(params);
      return { users: data.users, pagination: data.pagination };
    },
  });
}

export function useUserDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await usersApi.getById(id!);
      return data.user;
    },
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserData) => {
      const { data: result } = await usersApi.create(data);
      return result.user;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserData }) => {
      const { data: result } = await usersApi.update(id, data);
      return result.user;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useChangeRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const { data } = await usersApi.changeRole(id, role);
      return data.user;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { data } = await usersApi.activate(id, active);
      return data.user;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useTherapists() {
  return useQuery({
    queryKey: KEYS.therapists(),
    queryFn: async () => {
      const { data } = await usersApi.getTherapists();
      return data.therapists;
    },
  });
}

export function usePendingUsers() {
  return useQuery({
    queryKey: KEYS.pending(),
    queryFn: async () => {
      const { data } = await usersApi.getPending();
      return data.users;
    },
  });
}

export function useApproveUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, employmentRoleId, hospitalIds, primaryHospitalId }: {
      id: string;
      employmentRoleId?: string;
      hospitalIds?: string[];
      primaryHospitalId?: string;
    }) => {
      await usersApi.approve(id, { employmentRoleId, hospitalIds, primaryHospitalId });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useRejectUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await usersApi.reject(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      const { data: result } = await usersApi.updateProfile(data);
      return result.user;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.profile() });
    },
  });
}

export function useMyHospitals() {
  return useQuery({
    queryKey: KEYS.myHospitals(),
    queryFn: async () => {
      const { data } = await usersApi.getMyHospitals();
      return data.hospitals;
    },
  });
}

export function useSetPrimaryHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hospitalId: string) => {
      const { data } = await usersApi.setPrimaryHospital(hospitalId);
      return data.hospitals;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.myHospitals() });
    },
  });
}

export function useAddMyHospitals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hospitalIds: string[]) => {
      const { data } = await usersApi.addMyHospitals(hospitalIds);
      return data.hospitals;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.myHospitals() });
    },
  });
}

export function useRemoveMyHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (hospitalId: string) => {
      const { data } = await usersApi.removeMyHospital(hospitalId);
      return data.hospitals;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.myHospitals() });
    },
  });
}

export function useUserRelatedRecords(userId: string | null) {
  return useQuery({
    queryKey: KEYS.relatedRecords(userId!),
    queryFn: async () => {
      const { data } = await usersApi.getRelatedRecords(userId!);
      return { reports: data.reports, payrollEntries: data.payrollEntries, payrollRuns: data.payrollRuns };
    },
    enabled: !!userId,
  });
}

export function useBulkUserRelatedRecords(userIds: string[], enabled: boolean) {
  const results = useQueries({
    queries: userIds.map((id) => ({
      queryKey: KEYS.relatedRecords(id),
      queryFn: async () => {
        const { data } = await usersApi.getRelatedRecords(id);
        return { reports: data.reports, payrollEntries: data.payrollEntries, payrollRuns: data.payrollRuns };
      },
      enabled,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const totals = results.reduce(
    (acc, r) => {
      if (r.data) {
        acc.reports += r.data.reports;
        acc.payrollEntries += r.data.payrollEntries;
        acc.payrollRuns += r.data.payrollRuns;
      }
      return acc;
    },
    { reports: 0, payrollEntries: 0, payrollRuns: 0 },
  );
  const allLoaded = results.every((r) => r.isSuccess);

  return { isLoading, totals: allLoaded ? totals : null };
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await usersApi.delete(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateHospitals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, hospitalIds, primaryHospitalId }: { userId: string; hospitalIds: string[]; primaryHospitalId?: string }) => {
      await usersApi.updateHospitals(userId, hospitalIds, primaryHospitalId);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
